import { FederatedAuthentication } from '@nimbly-technologies/nimbly-common';
import { trackUserAction, trackOperation, trackUserInfo, logErrorWithContext } from '../../utils/sentry/sentrySetup';

import firebase from 'firebase';
import { isEmpty } from 'lodash';
import { Activity } from '@nimbly-technologies/nimbly-common';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Linking } from 'react-native';
import { connect } from 'react-redux';
import { firebaseConnect } from 'react-redux-firebase';
import { compose } from 'redux';
import { AUTH_MODE } from '../../constants/Constants';
import { LanguageType } from '../../constants/Languages';
import { RootState } from '../../store/reducers';
import { accountActions } from '../../store/reducers/account/account';
import { logActivityAsync } from '../../store/reducers/activity/activity.action';
import { landingActions } from '../../store/reducers/landing';
import { toastActions } from '../../store/reducers/toast';
import { ConnectedDispatch } from '../../typing/types';
import { handleConnectionTimeout } from '../../utils/handleConnectionTimeout';
import { isEmail } from '../../utils/validation';
import { authenticateWithCredentialStore } from './utils/loginWithCredentialStore';
import { AuthEmailRequest, requestAccountActivation, requestPasswordReset } from './utils/requestAuthEmail';
import AuthContainer from './AuthContainer';
import detectCapsLock from './utils/detectCapsLock';
import { authenticateFederatedCredentialsPopUp } from './utils/federated.service';
import { getLogoutOkta, requestEnrollment, verifyEnrollment } from './utils/federatedApiUtils';
import { findTenantByEmailDomain } from './utils/findTenantByEmailDomain';
import generateLoginErrorMessage from './utils/generateLoginErrorValidation';

import { selectProfile } from '../../store/selectors/profile';
// TEMP (hardcoded): federated SSO login is disabled. While false, the login form skips the
// `/v1.0/auth/federated/domain` tenant lookup + OKTA flow and signs in directly with
// email/username + password. Flip to true to restore federated login.
const FEDERATED_LOGIN_ENABLED: boolean = false;

type DispatchProps = ReturnType<typeof mapDispatchToProps>;

type AuthProps = {
  /** The redux Firebase prop */
  firebase: any;
  /** Real-time connectivity status - true if there's an internet connection */
  isConnected: boolean;
  /** Mode of the auth component - Sign Up, Login, or Reset Password */
  authMode: AUTH_MODE;
  /** Catching variable for any error caused by attempting to authenticate */
  authError: any;
  /** Language to be used throughout the app */
  language: LanguageType;
  /** Boolean true if the phone has a notch */
  hasNotch: boolean;
  /** The redux Firebase user object */
  auth: any;
  /** The user profile object */
  profile: any;
  /** Event fired when the Auth component is scrolled down */
  onScrollDown: () => void;
  setShowModalRequestAccess: (isVisible: boolean) => void;
} & ConnectedDispatch &
  DispatchProps;

export type AuthFormInput = {
  /** Value of the Email input field */
  email: string;
  /** Value of the Password input field */
  password: string;
  emailOrPhone: string;
  errorTextBottom: string;
};

export type AuthFormValidation = {
  /** True if email is valid */
  email: boolean;
  /** True if password is valid */
  password: boolean;
  emailOrPhone: boolean;
  /** Error text related to issues during auth - empty if there are no issues */
  errorText: string;
  errorTextBottom: string;
  isNeedAccessFederated: boolean;
};

/**
 * Forms and submission mechanism to authenticate a user
 *
 * Auth will allow the user to log in.
 * Once login/incoming user data is detected, Landing will forward the user to the next screen.
 *
 * It has 3 modes of operation:
 * - "signup" - user activates their account using their work email
 * - "login" - user logs in with email
 * - "resetpassword" - user requests for a reset password email to be sent
 */
const Auth = (props: AuthProps) => {
  const { t } = useTranslation(['auth', 'common']);
  const initialTempAuth = {
    tempToken: '',
    tenant: { domain: '', organizationID: '', tenantID: '', loginProtocol: '', loginProviderID: '' }
  };
  const [form, setForm] = useState<AuthFormInput>({
    email: '',
    password: '',
    emailOrPhone: '',
    errorTextBottom: ''
  });
  const [formValidation, setFormValidation] = useState<AuthFormValidation>({
    email: true,
    password: true,
    emailOrPhone: true,
    errorText: '',
    errorTextBottom: '',
    isNeedAccessFederated: false
  });
  const [_isBusy, setIsBusy] = useState<boolean>(false);
  const [_capsWarning, setCapsWarning] = useState<boolean>(false);
  const [_submitOnEnter, setSubmitOnEnter] = useState<boolean>(false);
  const [_emailSentSuccess, setEmailSentSuccess] = useState<boolean>(false);
  const [visibleTutorialModal, setVisibleTutorialModal] = useState<boolean>(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false);
  const [freezeBanner, setFreezeBanner] = useState<boolean>(false);
  const [isFederatedAuth, setIsFederatedAuth] = useState<boolean>(true);
  const [showPasswordField, setShowPasswordField] = useState<boolean>(false);
  const [tempAuth, setTempAuth] = useState<{ tempToken: string; tenant: FederatedAuthentication }>(initialTempAuth);
  const [pendingCreds, setPendingCreds] = useState<any>(null);

  const resetLoginState = () => {
    setShowPasswordField(false);
    setIsPasswordVisible(false);
    setIsFederatedAuth(true);
    setPendingCreds(null);
    setTempAuth(initialTempAuth);
  };

  const revealPasswordStep = () => {
    setIsFederatedAuth(false);
    setShowPasswordField(true);
    props.onScrollDown();
  };

  const clearFields = () => {
    setForm({
      email: '',
      password: '',
      emailOrPhone: '',
      errorTextBottom: ''
    });
    resetLoginState();
  };
  const clearFormValidation = () => {
    setFormValidation({
      email: true,
      password: true,
      emailOrPhone: true,
      errorText: '',
      errorTextBottom: '',
      isNeedAccessFederated: false
    });
  };

  const handleEmailTextInputFocused = () => {
    setSubmitOnEnter(false);
  };

  const handlePasswordTextInputFocused = () => {
    setSubmitOnEnter(true);
  };

  const handleEmailChange = (text: string) => {
    setForm((prevState: AuthFormInput) => ({
      ...prevState,
      email: text
    }));
    setFormValidation((prevState: AuthFormValidation) => ({
      ...prevState,
      email: true,
      emailOrPhone: true,
      errorText: '',
      errorTextBottom: ''
    }));
    setFreezeBanner(false);
  };

  const handleEmailOrPhoneChange = (text: string) => {
    if (!text.trim()) {
      resetLoginState();
    }

    setForm((prevState: AuthFormInput) => ({
      ...prevState,
      emailOrPhone: text
    }));
    setFormValidation((prevState: AuthFormValidation) => ({
      ...prevState,
      emailOrPhone: true,
      errorText: '',
      errorTextBottom: '',
      isNeedAccessFederated: false
    }));
    setFreezeBanner(false);
  };
  const handlePasswordChange = (text: string) => {
    const isUpperCase = detectCapsLock(text);
    setForm((prevState: AuthFormInput) => ({
      ...prevState,
      password: text
    }));
    setFormValidation((prevState: AuthFormValidation) => ({
      ...prevState,
      password: true,
      errorText: '',
      errorTextBottom: ''
    }));
    setCapsWarning(isUpperCase);
    setFreezeBanner(false);
  };

  const checkIsUserFederated = async (email: string) => {
    try {
      const getDomain = email.split('@').pop();
      const isEmailExist = await findTenantByEmailDomain(getDomain!, email);
      return isEmailExist;
    } catch (error) {
      console.error(error);
    }
  };

  const handleLogin = async () => {
    trackUserAction('login_attempt', { method: 'email_password' });
    props.dispatch(accountActions.isForceLogoutStatus(false));
    if (firebase.auth().currentUser) {
      firebase.auth().signOut();
    }
    setIsBusy(true);
    clearFormValidation();
    try {
      setFreezeBanner(false);
      await handleConnectionTimeout(attemptLogin());
    } catch (error) {
      if (FEDERATED_LOGIN_ENABLED && isFederatedAuth) return;
      setFormValidation({
        ...formValidation,
        errorText: t('auth:error.internetConnection')
      });
    }
    setIsBusy(false);
  };

  const authenticateFederatedCredentials = async (tenant: FederatedAuthentication) => {
    trackUserAction('federated_auth_attempt', { tenant_id: tenant?.tenantID });
    if (!firebase?.auth) return;
    try {
      const getTempToken = await authenticateFederatedCredentialsPopUp(tenant, firebase);
      const getIDToken = await getTempToken.user?.getIdTokenResult();
      if (!getIDToken) {
        clearFormValidation();
        setIsBusy(false);
        return;
      }

      const checkVerify = await verifyEnrollment(getIDToken.token, tenant!.tenantID!);
      if (checkVerify === 'not-approved' || checkVerify === 'user-enrolled' || checkVerify === 'not-enrolled') {
        getLogoutOkta();
        setTempAuth({ tempToken: getIDToken!.token, tenant });
        setFormValidation((prevState: AuthFormValidation) => ({
          ...prevState,
          isNeedAccessFederated: true,
          errorTextBottom: t('auth:validation.askPermision')
        }));
      }

      setIsBusy(false);
    } catch (err: any) {
      if (err.code === 'auth/account-exists-with-different-credential') {
        const pendingCred = err.credential;
        setIsFederatedAuth(false);
        setPendingCreds(pendingCred);
        setFormValidation((prevState: AuthFormValidation) => ({
          ...prevState,
          errorTextBottom: t('auth:validation.needPassword'),
          password: false
        }));
      }
      setIsBusy(false);
    }
  };

  const authPendingCredentials = async () => {
    if (!firebase?.auth) return;
    try {
      const auth = firebase.auth();
      setIsBusy(true);
      const methods = await auth.fetchSignInMethodsForEmail(form.emailOrPhone);
      if (methods[0] === 'password') {
        setIsFederatedAuth(false);
        const result = await authenticateWithCredentialStore(form.emailOrPhone, form.password, customToken =>
          auth.signInWithCustomToken(customToken)
        );
        if (result.kind !== 'authenticated') {
          handleLoginError(result);
          return;
        }
        if (auth.currentUser) {
          await auth.currentUser.linkWithCredential(pendingCreds);
        }
        return;
      }
    } catch (error) {
      setIsBusy(false);
      handleLoginError(error);
    }
  };

  const rejectOrganizationTenantPasswordLogin = () => {
    // Tenant-aware custom-token minting does not exist yet. This flow is
    // hardcoded off today; if it is enabled later, fail closed instead of
    // restoring Firebase as an independent password authority.
    handleLoginError({
      code: 'auth/network-request-failed',
      message: 'Organization tenant sign-in is temporarily unavailable.'
    });
    setIsBusy(false);
  };

  const attemptLogin = async () => {
    const identifier = form.emailOrPhone.trim();

    if (isEmail(identifier)) {
      if (!FEDERATED_LOGIN_ENABLED) {
        await loginEmailAndPassword(identifier, form.password);
        return;
      }

      const tenantLookup = await checkIsUserFederated(identifier);
      if (tenantLookup === undefined) {
        return;
      }

      if (pendingCreds) {
        revealPasswordStep();
        await authPendingCredentials();
        return;
      }

      if (tenantLookup?.isFederated === true && tenantLookup?.isOrganizationTenant === false) {
        setShowPasswordField(false);
        setIsFederatedAuth(true);
        await authenticateFederatedCredentials(tenantLookup.federatedAuthentication as FederatedAuthentication);
        return;
      }

      revealPasswordStep();

      if (!form.password) {
        return;
      }

      if (tenantLookup?.isFederated === false && tenantLookup?.isOrganizationTenant === true) {
        rejectOrganizationTenantPasswordLogin();
        return;
      }

      await loginEmailAndPassword(identifier, form.password);
      return;
    }

    if (isEmpty(identifier)) {
      setFormValidation((prevState: AuthFormValidation) => ({
        ...prevState,
        emailOrPhone: false,
        errorText: t('auth:error.requiredEmailOrPhone')
      }));
      return;
    }

    // api-users resolves username identifiers and mints the Firebase custom
    // token for the matching uid; the client must not rewrite or re-verify it.
    await loginEmailAndPassword(identifier, form.password);
  };
  /**
   * Sign in through the authoritative credential store, then redeem its
   * Firebase custom token for the RTDB/Storage session the app still needs.
   *
   * The credential store has been authoritative for every organization since
   * 2026-08-25, so Firebase is asked to open a session here, not to check the
   * password. The session carries the same uid and
   * `organization` claim as a password sign-in, so nothing downstream - the ID
   * token on every API call, RTDB, Storage - can tell the difference.
   */
  const loginEmailAndPassword = async (identifier: string, password: string) => {
    if (!password) {
      setFormValidation((prevState: AuthFormValidation) => ({
        ...prevState,
        password: false,
        errorText: t('auth:error.passwordMissed')
      }));
      return;
    }

    const result = await authenticateWithCredentialStore(identifier, password, customToken =>
      firebase.auth().signInWithCustomToken(customToken)
    );

    if (result.kind === 'authenticated') {
      trackOperation('user_login', 'success', { method: 'custom_token' });
      return;
    }

    if (result.kind === 'unavailable' && result.stage === 'custom-token-exchange') {
      const error = result.cause;
      logErrorWithContext(error instanceof Error ? error : new Error(String(error)), 'auth_custom_token_error', {
        component: 'Auth.tsx'
      });
    }

    handleLoginError(result);
  };
  const handleLoginError = (error: any) => {
    trackOperation('user_login', 'failure', {
      error_code: error.code
    });
    logErrorWithContext(error, 'auth_login_error', {
      error_code: error.code,
      component: 'Auth.tsx'
    });
    if (error.code && error.code === 'auth/user-disabled') {
      setFreezeBanner(true);
    }
    setFormValidation(generateLoginErrorMessage(error));
  };

  const handleAttemptEmailSignup = async () => {
    setIsBusy(true);
    try {
      await handleConnectionTimeout(attemptVerifyAccount());
    } catch (error) {
      setFormValidation((prevState: AuthFormValidation) => ({
        ...prevState,
        errorText: t('auth:error.internetConnection')
      }));
    }
    setIsBusy(false);
  };

  /**
   * What to put on the form when a mail request did not go out.
   *
   * api-users answers 401 for both halves of "we do not know this address" -
   * no such user, and not an address at all - and that is the case a person
   * actually hits, so it keeps the localized sentence the Firebase path used
   * to show for `auth/user-not-found`. The server's own message is English,
   * and most of this user base does not read it.
   *
   * Every other refusal - disabled, rate limited - has no localized equivalent
   * here, so it shows what the server said, which is what this screen did for
   * those cases before. An unreachable API is never reported as a bad address.
   */
  const authEmailErrorText = (result: AuthEmailRequest): string => {
    if (result.kind !== 'refused') {
      return t('auth:error.internetConnection');
    }
    if (result.status === 401) {
      return t('auth:validation.noUserFound');
    }
    return result.message || t('auth:validation.defaultValidation');
  };

  const sendEmailVerification = async (email: string) => {
    // api-users sends the activation link and owns the endpoint that redeems
    // it, so the password set through that link is mirrored into the
    // credential store. The Firebase call this replaced mailed a link to
    // `nimbly.page.link/openapp`, a dynamic link that has been dead since
    // Firebase shut the service down, aimed at the native app's bundle IDs.
    const result = await requestAccountActivation(email);

    if (result.kind === 'sent') {
      setEmailSentSuccess(true);
      clearFormValidation();
      return;
    }

    logErrorWithContext(new Error(result.message || `activation email ${result.kind}`), 'auth_activation_email_error', {
      outcome: result.kind,
      component: 'Auth.tsx'
    });
    setFormValidation((prevState: AuthFormValidation) => ({
      ...prevState,
      email: false,
      emailOrPhone: false,
      errorText: authEmailErrorText(result)
    }));
  };
  const attemptVerifyAccount = async () => {
    const { isConnected } = props;
    if (!isConnected) {
      setFormValidation((prevState: AuthFormValidation) => ({
        ...prevState,
        errorText: t('auth:error.offline')
      }));
    } else if (isEmail(form.emailOrPhone)) {
      if (isEmpty(form.emailOrPhone)) {
        setFormValidation((prevState: AuthFormValidation) => ({
          ...prevState,
          emailOrPhone: false,
          errorText: t('auth:error.invalidEmail')
        }));
      } else {
        await sendEmailVerification(form.emailOrPhone);
      }
    } else {
      setFormValidation((prevState: AuthFormValidation) => ({
        ...prevState,
        emailOrPhone: false,
        errorText: t('auth:error.requiredEmailOrPhone')
      }));
    }
  };
  const handleChangeSectionForgotPassword = () => {
    resetLoginState();
    setFormValidation((prevState: AuthFormValidation) => ({
      ...prevState,
      email: true,
      password: true,
      errorText: '',
      errorTextBottom: ''
    }));
    props.setAuthMode(AUTH_MODE.RESET_PASSWORD);
  };

  const handleSendPasswordResetRequest = async () => {
    if (!form.email) {
      return;
    }

    setIsBusy(true);

    // Through api-users rather than `firebase.auth().sendPasswordResetEmail`.
    // Firebase's own link lands on Firebase's action page, which writes the new
    // password into Firebase and nowhere else - so the credential store keeps
    // the old one, and login is only admitted by the Firebase fallback behind
    // `CUSTOM_AUTH_FIREBASE_FALLBACK`. Every reset sent that way is one more
    // account that gets refused the day that fallback goes off.
    let result: AuthEmailRequest;
    try {
      result = await handleConnectionTimeout(requestPasswordReset(form.email));
    } catch {
      // The 10s race lost, not a refusal. The request is still in flight and
      // the mail may well go out; all we can honestly say is to try again.
      setIsBusy(false);
      setFormValidation((prevState: AuthFormValidation) => ({
        ...prevState,
        errorText: t('auth:error.internetConnection')
      }));
      return;
    }

    setIsBusy(false);

    if (result.kind === 'sent') {
      clearFields();
      trackOperation('password_reset_request', 'success', {});
      props.setAuthMode(AUTH_MODE.LOGIN);
      props.createToast(t('auth:success'));
      return;
    }

    logErrorWithContext(
      new Error(result.message || `password reset email ${result.kind}`),
      'auth_password_reset_email_error',
      { outcome: result.kind, component: 'Auth.tsx' }
    );
    trackOperation('password_reset_request', 'failure', { error_code: result.kind });
    setFormValidation((prevState: AuthFormValidation) => ({
      ...prevState,
      errorText: authEmailErrorText(result)
    }));
  };

  const handleChangeSectionSignup = () => {
    resetLoginState();
    clearFormValidation();
    setEmailSentSuccess(false);
    setFreezeBanner(false);
    props.setAuthMode(AUTH_MODE.SIGN_UP);
  };

  const handleChangeSectionLogin = () => {
    resetLoginState();
    clearFormValidation();
    setEmailSentSuccess(false);
    setFreezeBanner(false);
    props.setAuthMode(AUTH_MODE.LOGIN);
  };

  const handleWhatsAppSupport = () =>
    Linking.openURL('https://wa.me/6282123069155').catch(() => {
      props.createToast(t('auth:error.whatsapp'));
    });

  const handleTogglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  const onSubmitRequestFederated = async () => {
    const reCheckIsFederatedUser = await checkIsUserFederated(form.emailOrPhone);

    if (!reCheckIsFederatedUser.isFederated) {
      setIsFederatedAuth(false);
      setFormValidation((prevState: AuthFormValidation) => ({
        ...prevState,
        isNeedAccessFederated: false
      }));
      return;
    }

    setIsBusy(true);

    try {
      await requestEnrollment(tempAuth.tempToken, tempAuth.tenant);
      props.setShowModalRequestAccess(true);
      clearFormValidation();
      clearFields();
      setIsBusy(false);
    } catch (e) {
      console.error(e);
      setIsBusy(false);
    }
  };

  return (
    <AuthContainer
      hasPendingCreds={!!pendingCreds}
      hasNotch={props.hasNotch}
      language={props.language}
      authMode={props.authMode}
      freezeBanner={freezeBanner}
      onSubmitRequestFederated={onSubmitRequestFederated}
      isFederatedAuth={isFederatedAuth}
      showPasswordField={showPasswordField}
      form={form}
      formValidation={formValidation}
      isPasswordVisible={isPasswordVisible}
      _isBusy={_isBusy}
      _capsWarning={_capsWarning}
      _emailSentSuccess={_emailSentSuccess}
      _submitOnEnter={_submitOnEnter}
      onFocusMovedDown={props.onScrollDown}
      onChangeSectionSignup={handleChangeSectionSignup}
      onChangeSectionLogin={handleChangeSectionLogin}
      onChangeSectionForgotPassword={handleChangeSectionForgotPassword}
      onEmailChange={handleEmailChange}
      onPasswordChange={handlePasswordChange}
      onEmailOrPhoneChange={handleEmailOrPhoneChange}
      onEmailTextInputFocused={handleEmailTextInputFocused}
      onPasswordTextInputFocused={handlePasswordTextInputFocused}
      onSubmitSignup={handleAttemptEmailSignup}
      onSubmitLogin={handleLogin}
      onSubmitPasswordReset={handleSendPasswordResetRequest}
      onContactWhatsAppSupport={handleWhatsAppSupport}
      visibleTutorialModal={visibleTutorialModal}
      handleVisibleTutorialModal={setVisibleTutorialModal}
      onTogglePasswordVisibility={handleTogglePasswordVisibility}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  isConnected: state.network.isConnected,
  authMode: state.landing.authMode,
  authError: state.firebase.authError,
  auth: state.firebase.auth,
  profile: selectProfile(state),
  language: state.landing.language,
  hasNotch: state.account.hasNotch
});

const mapDispatchToProps = (dispatch: any) => ({
  setAuthMode: (authMode: AUTH_MODE) => dispatch(landingActions.setAuthMode(authMode)),
  createToast: (message: string) => dispatch(toastActions.createToast(message, true, 10000)),
  logActivity: (data: Activity) => dispatch(logActivityAsync.request(data))
});

export default compose(firebaseConnect(), connect(mapStateToProps, mapDispatchToProps))(Auth) as any;
